import { useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
} from '@mui/material';
import { useGetIdentity, useNotify } from 'react-admin';
import { Send, Bug } from 'lucide-react';
import { supabase } from '../providers/supabase/supabase';

interface BugReportDialogProps {
    open: boolean;
    onClose: () => void;
    screenshotBlob: Blob | null;
    formData: Record<string, unknown> | null;
}

export default function BugReportDialog({
    open,
    onClose,
    screenshotBlob,
    formData,
}: BugReportDialogProps) {
    const { identity } = useGetIdentity();
    const notify = useNotify();
    const [description, setDescription] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const screenshotPreview = screenshotBlob
        ? URL.createObjectURL(screenshotBlob)
        : null;

    const handleSubmit = async () => {
        if (!description.trim()) {
            notify('Please describe the bug', { type: 'warning' });
            return;
        }
        if (!identity) {
            notify('Not authenticated', { type: 'error' });
            return;
        }

        setSubmitting(true);
        try {
            // Get the actual auth user ID (identity.id is the sales record ID)
            const {
                data: { user: authUser },
            } = await supabase.auth.getUser();
            if (!authUser) {
                notify('Not authenticated', { type: 'error' });
                setSubmitting(false);
                return;
            }

            let screenshotUrl: string | null = null;

            // Upload screenshot to Supabase Storage
            if (screenshotBlob) {
                const fileName = `${authUser.id}/${Date.now()}.png`;
                const { error: uploadError } = await supabase.storage
                    .from('bug-screenshots')
                    .upload(fileName, screenshotBlob, {
                        contentType: 'image/png',
                    });

                if (uploadError) {
                    console.error('Screenshot upload error:', uploadError);
                } else {
                    const {
                        data: { publicUrl },
                    } = supabase.storage
                        .from('bug-screenshots')
                        .getPublicUrl(fileName);
                    screenshotUrl = publicUrl;
                }
            }

            // Insert bug report
            const { error: insertError } = await supabase
                .from('bug_reports')
                .insert({
                    user_id: authUser.id,
                    user_email:
                        identity.email || identity.fullName || 'unknown',
                    description: description.trim(),
                    screenshot_url: screenshotUrl,
                    page_url: window.location.href,
                    form_data: formData,
                    status: 'new',
                });

            if (insertError) {
                throw insertError;
            }

            // Fire-and-forget ntfy alert. Not awaited so a slow ntfy.sh never
            // blocks the submit flow, and a failure here must never surface as a
            // failed bug report (the insert already succeeded above).
            void fetch('/.netlify/functions/bug-notify', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    description: description.trim(),
                    user_email:
                        identity.email || identity.fullName || 'unknown',
                    user_name: identity.fullName,
                    company_name: identity.company_name,
                    agent_number: identity.agent_number,
                    page_url: window.location.href,
                    origin: window.location.origin,
                }),
            }).catch(() => {});

            notify('Bug report submitted! We will look into it.', {
                type: 'success',
            });
            setDescription('');
            onClose();
        } catch (err) {
            console.error('Bug report submit error:', err);
            notify('Failed to submit bug report. Please try again.', {
                type: 'error',
            });
        } finally {
            setSubmitting(false);
        }
    };

    const handleClose = () => {
        if (!submitting) {
            setDescription('');
            onClose();
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            aria-labelledby="bug-report-dialog-title"
        >
            <DialogTitle
                id="bug-report-dialog-title"
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    borderBottom: '1px solid #e2e8f0',
                    pb: 1.5,
                }}
            >
                <Bug size={20} color="#ef4444" />
                <Typography
                    variant="h6"
                    component="span"
                    sx={{ fontWeight: 600 }}
                >
                    Report a Bug
                </Typography>
            </DialogTitle>
            <DialogContent sx={{ pt: '16px !important' }}>
                {/* Screenshot Preview */}
                {screenshotPreview && (
                    <Box sx={{ mb: 2 }}>
                        <Typography
                            variant="body2"
                            sx={{
                                color: '#64748b',
                                mb: 1,
                                fontWeight: 500,
                            }}
                        >
                            Screenshot captured:
                        </Typography>
                        <Box
                            component="img"
                            src={screenshotPreview}
                            alt="Screenshot of current page"
                            sx={{
                                width: '100%',
                                maxHeight: 200,
                                objectFit: 'contain',
                                borderRadius: '8px',
                                border: '1px solid #e2e8f0',
                                backgroundColor: '#f8fafc',
                            }}
                        />
                    </Box>
                )}

                {/* Form data indicator */}
                {formData && Object.keys(formData).length > 0 && (
                    <Box
                        sx={{
                            mb: 2,
                            p: 1.5,
                            bgcolor: '#f0fdf4',
                            borderRadius: '8px',
                            border: '1px solid #bbf7d0',
                        }}
                    >
                        <Typography
                            variant="body2"
                            sx={{ color: '#166534', fontWeight: 500 }}
                        >
                            Form data has been automatically captured and will
                            be included with your report.
                        </Typography>
                    </Box>
                )}

                {/* Bug Description */}
                <TextField
                    autoFocus
                    label="Describe the bug"
                    placeholder="What happened? What did you expect to happen?"
                    fullWidth
                    multiline
                    minRows={4}
                    maxRows={8}
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                    disabled={submitting}
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            borderRadius: '8px',
                        },
                    }}
                />
            </DialogContent>
            <DialogActions
                sx={{
                    px: 3,
                    pb: 2,
                    borderTop: '1px solid #e2e8f0',
                    pt: 1.5,
                }}
            >
                <Button
                    onClick={handleClose}
                    disabled={submitting}
                    sx={{ color: '#64748b' }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={submitting || !description.trim()}
                    startIcon={
                        submitting ? (
                            <CircularProgress size={16} />
                        ) : (
                            <Send size={16} />
                        )
                    }
                    sx={{
                        bgcolor: '#ef4444',
                        '&:hover': { bgcolor: '#dc2626' },
                        borderRadius: '8px',
                    }}
                >
                    {submitting ? 'Submitting...' : 'Submit Bug Report'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
